import {
  CloseOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  Input,
  Select,
  Form,
  notification,
  PageHeader,
  Col,
  Row,
  Radio,
  InputNumber,
  Space,
  TimePicker,
} from "antd";
import moment from 'moment';
import { useEffect, useRef, useState } from "react";
import { Prompt, useHistory } from "react-router-dom";
import IconLand from "../../static/icon/IconLand";
import { useGoogleMaps } from "react-hook-google-maps";
import "../../Tools/customStyles.css";
import { useAuth } from "../../context/auth";
import MerchantDropdown from "../Dropdown/MerchantDropdown";
import { listLandlordBusinessHoursType, listUserType } from "../../utils/formatter";
import ProvinceDropdown from "../Dropdown/ProvinceDropdown";

const GoogleMap = ({
  form,
  latitude: lat,
  longitude: lng,
}) => {
  const { ref, map, google } = useGoogleMaps(
    "AIzaSyAsqXNWAIUQAIMnGnf2W0vLN04Lftgvrcw",
    {
      center: { lat: lat ?? 13.69136766, lng: lng ?? 100.53826492 },
      zoom: 10,
    },
  );
  const prevMarkersRef = useRef([]);
  useEffect(() => {
    const center = { lat: lat ?? 13.69136766, lng: lng ?? 100.53826492 };

    if (map) {
      const createMarker = (position) => new google.maps.Marker({ position, map });
      const clearMarkers = () => prevMarkersRef.current.forEach(m => m.setMap(null));

      clearMarkers(prevMarkersRef.current);

      google.maps.event.addListener(map, "click", (e) => {
        const m = createMarker({ lat: e.latLng.lat(), lng: e.latLng.lng() });
        map.setCenter({ lat: e.latLng.lat(), lng: e.latLng.lng() });
        clearMarkers(prevMarkersRef.current);
        prevMarkersRef.current.push(m);
        form.setFieldsValue({
          latitude: e.latLng.lat(),
          longitude: e.latLng.lng(),
        });
      });

      const m = createMarker({ lat, lng });
      map.setCenter(center);
      prevMarkersRef.current.push(m);
    }
  }, [map, form, lat, lng, google]);

  return <div id="map" className="w-full h-80 rounded-2xl" ref={ref} />;
};

const LandlordSetting = ({ match: { params: { id } } }) => {
  const history = useHistory();
  const { accessToken, adminRole } = useAuth();

  const [form] = Form.useForm();
  const businessHoursType = Form.useWatch('businessHoursType', form);
  const latitude = Form.useWatch('latitude', form);
  const longitude = Form.useWatch('longitude', form);

  const [change, setChange] = useState(false);
  const [disabled, setDisabled] = useState(false);

  useEffect(() => {
    if (id === 'new') return;

    async function getLandlord() {
      const url = new URL('/api/landlord/' + id, window.location);

      const res = await fetch(url, {
        headers: { 'X-ACCESS-TOKEN': accessToken },
      });
      if (res.ok) {
        const { landlord } = await res.json();
        for (const businessHour of landlord.businessHours) {
          businessHour.openTime = moment(businessHour.openTime);
          businessHour.closeTime = moment(businessHour.closeTime);
        }
        form.setFieldsValue(landlord);
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }
    }

    setChange(false);
    getLandlord();
  }, [accessToken, form, id]);

  const save = async (values) => {
    setDisabled(true);
    setChange(false);

    const url = new URL(id === 'new' ? '/api/landlord' : '/api/landlord/' + id, window.location);

    const res = await fetch(url, {
      method: id === 'new' ? 'POST' : 'PATCH',
      headers: {
        'X-ACCESS-TOKEN': accessToken,
        'content-type': 'application/json',
      },
      body: JSON.stringify(values),
    });
    if (res.ok) {
      notification["success"]({
        message: "Successfully saved data.",
      });
      history.push({
        pathname: "/landlord",
        searchValue: history.location.searchValue,
        pageValue: history.location.pageValue,
      });
    } else {
      notification["error"]({
        message: "Something went wrong !",
        description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
      });
    }

    setDisabled(false);
    setChange(true);
  };

  return (
    <>
      <div className="flex justify-between items-center mt-2">
        <PageHeader
          style={{ padding: 0, width: "100%" }}
          title={
            <span className="flex items-center mt-2">
              <IconLand /> &nbsp;&nbsp;Site Setting
            </span>
          }
        />
      </div>
      <div className="content">
        <div className="search">
          <Form
            form={form}
            onFinish={save}
            onFieldsChange={() => setChange(true)}
            className="search_inline row m-auto w-3/4 py-6"
          >
            <Row gutter={40} style={{ marginTop: 30 }}>
              {['admin', 'callcenter'].includes(adminRole) && (
                <Col sm={24} md={24} lg={12}>
                  <Form.Item
                    label="Cluster Name"
                    name="merchantId"
                    rules={[
                      {
                        required: true,
                        message: "Site Name (EN) is required.",
                      },
                    ]}
                    colon={false}
                    className="mt-2"
                  >
                    <MerchantDropdown
                      disabled={id !== 'new'}
                    />
                  </Form.Item>
                </Col>
              )}
              <Col sm={24} md={24} lg={12}>
                <Form.Item
                  label="Site ID"
                  name="id"
                  colon={false}
                  className="mt-2"
                >
                  <Input
                    placeholder="Auto generate"
                    maxLength={50}
                    disabled={true}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={40} style={{ marginTop: 15 }}>
              <Col sm={24} md={24} lg={12}>
                <Form.Item
                  label="Site Name (EN)"
                  name="nameEn"
                  rules={[
                    {
                      required: true,
                      message: "Site Name (EN) is required.",
                    },
                  ]}
                  colon={false}
                  className="mt-2"
                >
                  <Input
                    placeholder="Site Name (EN)"
                    maxLength={255}
                  />
                </Form.Item>
              </Col>
              <Col sm={24} md={24} lg={12}>
                <Form.Item
                  label="Site Name (TH)"
                  name="nameTh"
                  rules={[
                    {
                      required: true,
                      message: "Site Name (TH) is required.",
                    },
                  ]}
                  colon={false}
                  className="mt-2"
                >
                  <Input
                    placeholder="Site Name (TH)"
                    maxLength={255}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={40} style={{ marginTop: 10 }}>
              <Col sm={24} md={24} lg={12}>
                <Row gutter={40}>
                  <Col sm={24} md={24} lg={12}>
                    <Form.Item
                      label="Latitude"
                      name="latitude"
                      rules={[
                        {
                          required: true,
                          message: "Latitude is required.",
                        },
                      ]}
                      colon={false}
                      className="mt-2"
                    >
                      <InputNumber
                        placeholder="Latitude"
                        className="w-full"
                      />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={24} lg={12}>
                    <Form.Item
                      label="Longitude"
                      name="longitude"
                      rules={[
                        {
                          required: true,
                          message: "Longitude is required.",
                        },
                      ]}
                      colon={false}
                      className="mt-2"
                    >
                      <InputNumber
                        placeholder="Longitude"
                        className="w-full"
                      />
                    </Form.Item>
                  </Col>
                  <Col sm={24} md={24} lg={24}>
                    <Form.Item
                      label="Pin Location"
                      colon={false}
                      className="mt-2"
                    >
                      <GoogleMap
                        form={form}
                        latitude={latitude}
                        longitude={longitude}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
              <Col sm={24} md={24} lg={12}>
                <Form.Item
                  label="Business Hours"
                  name="businessHoursType"
                  rules={[
                    {
                      required: true,
                      message: "Business Hours is required.",
                    },
                  ]}
                  colon={false}
                  className="mt-2"
                >
                  <Radio.Group
                    options={listLandlordBusinessHoursType.map(type => ({
                      value: type.name,
                      label: type.value,
                    }))}
                  />
                </Form.Item>
                {businessHoursType === 'period' && (
                  <Form.List
                    name="businessHours"
                  >
                    {(fields, { add, remove }) => (
                      <div className="w-full">
                        {fields.map(({ name }) => (
                          <Space key={name} style={{ display: "flex" }} align="baseline" >
                            <Form.Item
                              name={[name, "dayOfWeek"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Day is required.",
                                },
                              ]}
                              className="w-32 mb-1"
                            >
                              <Select
                                placeholder="Day"
                                options={[
                                  { value: 1, label: 'Sunday' },
                                  { value: 2, label: 'Monday' },
                                  { value: 3, label: 'Tuesday' },
                                  { value: 4, label: 'Wednesday' },
                                  { value: 5, label: 'Thurday' },
                                  { value: 6, label: 'Friday' },
                                  { value: 7, label: 'Saturday' },
                                ]}
                              />
                            </Form.Item>
                            <Form.Item
                              name={[name, "openTime"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Time is required.",
                                },
                              ]}
                              className="w-32 mb-1"
                            >
                              <TimePicker
                                placeholder="Open"
                                format={"HH:mm"}
                                style={{ width: "100%" }}
                              />
                            </Form.Item>
                            <Form.Item
                              name={[name, "closeTime"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Time is required.",
                                },
                              ]}
                              className="w-32 mb-1"
                            >
                              <TimePicker
                                placeholder="Closing"
                                format={"HH:mm"}
                                style={{ width: "100%" }}
                              />
                            </Form.Item>
                            <Button
                              onClick={() => remove(name)}
                              icon={<CloseOutlined />}
                            />
                          </Space>
                        ))}
                        <Button
                          type="primary"
                          onClick={() => add()}
                          icon={<PlusOutlined />}
                          className="w-full"
                        />
                      </div>
                    )}
                  </Form.List>
                )}
              </Col>
            </Row>

            <div className="flex items-center -mx-2.5 mb-2">
              <div className="flex-1 border-b border-gray-300" />
              <span className="px-2.5 pb-1 font-black">
                EVLP
              </span>
              <div className="flex-1 border-b border-gray-300" />
            </div>

            <Row gutter={40} style={{ marginTop: 20 }}>
              <Col sm={24} md={24} lg={12}>
                <Form.Item
                  label="MEA ID"
                  name="meaId"
                  colon={false}
                  className="mt-2"
                >
                  <Input
                    placeholder="MEA ID"
                    maxLength={255}
                  />
                </Form.Item>
              </Col>
              <Col sm={24} md={24} lg={12}>
                <Form.Item
                  label="PEA ID"
                  name="peaId"
                  colon={false}
                  className="mt-2"
                >
                  <Input
                    placeholder="PEA ID"
                    maxLength={255}
                  />
                </Form.Item>
              </Col>
            </Row>

            <div className="flex items-center -mx-2.5 mb-2">
              <div className="flex-1 border-b border-gray-300" />
              <span className="px-2.5 pb-1 font-black">
                Partner
              </span>
              <div className="flex-1 border-b border-gray-300" />
            </div>

            <Row gutter={40} style={{ marginTop: 20 }}>
              <Col sm={24} md={24} lg={12}>
                <Form.Item
                  label="Project ID"
                  name="partnerProjectId"
                  colon={false}
                  className="mt-2"
                >
                  <Input
                    placeholder="Project ID"
                    maxLength={255}
                  />
                </Form.Item>
              </Col>
              <Col sm={24} md={24} lg={12}>
                <Form.Item
                  label="Type"
                  name="partnerUserType"
                  colon={false}
                  className="mt-2"
                >
                  <Select
                    placeholder="Type"
                    options={listUserType.filter(type => type.name !== 'mobile').map(type => ({
                      value: type.name,
                      label: type.value,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            <div className="flex items-center -mx-2.5 mb-2">
              <div className="flex-1 border-b border-gray-300" />
              <span className="px-2.5 pb-1 font-black">
                Other Infomation
              </span>
              <div className="flex-1 border-b border-gray-300" />
            </div>


            <Row gutter={40} style={{ marginTop: 15 }}>
              <Col sm={24} md={24} lg={12}>
                <Form.Item
                  label="Site Description (EN)"
                  name="descEn"
                  colon={false}
                  className="mt-2"
                >
                  <Input.TextArea
                    placeholder="Site Description (EN)"
                    autoSize={{ minRows: 3, maxRows: 5 }}
                    maxLength={255}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col sm={24} md={24} lg={12}>
                <Form.Item
                  label="Site Description (TH)"
                  name="descTh"
                  colon={false}
                  className="mt-2"
                >
                  <Input.TextArea
                    placeholder="Site Description(TH)"
                    autoSize={{ minRows: 3, maxRows: 5 }}
                    maxLength={255}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={40} style={{ marginTop: 20 }}>
              <Col sm={24} md={24} lg={12}>
                <Form.Item
                  label="Address (EN)"
                  name="addrEn"
                  colon={false}
                  className="mt-2"
                >
                  <Input.TextArea
                    placeholder="Address (EN)"
                    autoSize={{ minRows: 3, maxRows: 5 }}
                    maxLength={255}
                  />
                </Form.Item>
              </Col>
              <Col sm={24} md={24} lg={12}>
                <Form.Item
                  label="Address (TH)"
                  name="addrTh"
                  colon={false}
                  className="mt-2"
                >
                  <Input.TextArea
                    placeholder="Address (TH)"
                    autoSize={{ minRows: 3, maxRows: 5 }}
                    maxLength={255}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={40} style={{ marginTop: 15 }}>
              <Col sm={24} md={24} lg={12}>
                <Form.Item
                  label="Province"
                  name="provinceId"
                  colon={false}
                  className="mt-2"
                >
                  <ProvinceDropdown />
                </Form.Item>
              </Col>
              <Col sm={24} md={24} lg={12}>
                <Form.Item
                  label="Tel"
                  name="tel"
                  colon={false}
                  className="mt-2"
                >
                  <Input
                    placeholder="Tel"
                    maxLength={13}
                  />
                </Form.Item>
              </Col>
            </Row>

            <div className="m-auto pt-6 flex justify-center space-x-4">
              <Button
                className="w-36"
                type="primary"
                ghost
                shape="round"
                htmlType="button"
                onClick={() => {
                  history.push({
                    pathname: "/landlord",
                    searchValue: history.location.searchValue,
                    pageValue: history.location.pageValue,
                  });
                }}
              >
                Cancel
              </Button>
              <Button
                className="w-36"
                key="save"
                type="primary"
                shape="round"
                htmlType="submit"
                disabled={disabled}
              >
                Save
              </Button>
            </div>
          </Form>
        </div>
      </div>
      <Prompt when={change} message="Changes will not be saved. Do you want to proceed ?" />
    </>
  );
};
export default LandlordSetting;
